import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { serialize } from 'object-to-formdata';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import cn from 'classnames';

import { useHttp } from 'hooks/http.hook';
import { useInitialDocument } from 'hooks/documents';

import { errorToRender } from 'helpers/errors';
import GeneralInformation from 'components/TransportComponents/GeneralInformation';
import StepChips, { StepChip } from 'components/common/StepChips';
import Loading from 'components/Loading';
import Error from 'components/Forms/Error';
import Transport, { initializeTransportFields } from 'models/transport';
import { TransportService } from 'service/TransportService';
import { TRANSPORT as TRANSPORT_API } from 'endpointsConst';
import { PROFILE } from 'helpers/routes';

import { validationSchema } from './validate';
import { TransportDictionariesProvider, useTransportDictionaries, labelsFor } from './dictionaries';
import KindStep from './steps/KindStep';
import TypeStep from './steps/TypeStep';
import SizesStep from './steps/SizesStep';
import ExtraStep from './steps/ExtraStep';

import styles from './styles.module.scss';

const arrowLeftIcon = new URL('../../../../img/arrow-left-blue.svg', import.meta.url).href;
const arrowRightIcon = new URL('../../../../img/arrow-right-white.svg', import.meta.url).href;

const STEPS = ['kind', 'type', 'sizes', 'extra'] as const;

type StepId = (typeof STEPS)[number];

/** Какому шагу принадлежит поле — чтобы после неудачной отправки открыть шаг с ошибкой */
const STEP_FIELDS: Record<StepId, string[]> = {
  kind: ['transport_bodytype_additionals_id'],
  type: ['transport_bodytype_ids'],
  sizes: ['carrying_capacity', 'volume', 'length', 'width', 'height'],
  extra: ['transports_loading_types', 'trailer_registration_number', 'notes'],
};

const SIZE_FIELDS = STEP_FIELDS.sizes;

interface AddTransportProps {
  onClose?: () => void;
  onSuccess?: () => void;
  editId?: string | number;
}

interface StepsBodyProps {
  values: any;
  errors: Record<string, unknown>;
  submitCount: number;
  activeStep: StepId;
  setActiveStep: (step: StepId) => void;
}

/** Чипы шагов + содержимое активного шага. Внутри Formik, поэтому видит values/errors. */
const StepsBody = ({ values, errors, submitCount, activeStep, setActiveStep }: StepsBodyProps) => {
  const { t } = useTranslation();
  const { kinds, bodyTypes, loadingTypes } = useTransportDictionaries();

  // После неудачной отправки открываем первый шаг, на котором есть ошибка
  useEffect(() => {
    if (submitCount === 0) return;
    const failed = STEPS.find((step) => STEP_FIELDS[step].some((field) => errors[field]));
    if (failed) {
      setActiveStep(failed);
    }
  }, [submitCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const summaries = useMemo<Record<StepId, string>>(() => {
    const filledSizes = SIZE_FIELDS.filter((field) => values[field]).length;
    return {
      kind: labelsFor(kinds, [values.transport_bodytype_additionals_id]).join(', '),
      type: labelsFor(bodyTypes, values.transport_bodytype_ids).join(', '),
      sizes: filledSizes ? t('transportFields.steps.summary_filled', { count: filledSizes }) : '',
      extra: labelsFor(loadingTypes, values.transports_loading_types).join(', '),
    };
  }, [values, kinds, bodyTypes, loadingTypes, t]);

  const chips: StepChip[] = STEPS.map((step) => ({
    id: step,
    label: t(`transportFields.steps.${step}`),
    summary: summaries[step],
  }));

  const activeIndex = STEPS.indexOf(activeStep);

  return (
    <>
      <StepChips
        steps={chips}
        activeId={activeStep}
        onSelect={(id) => setActiveStep(id as StepId)}
      />

      <div className={styles.stepContent}>
        {activeStep === 'kind' && <KindStep />}
        {activeStep === 'type' && <TypeStep />}
        {activeStep === 'sizes' && <SizesStep />}
        {activeStep === 'extra' && <ExtraStep />}
      </div>

      <div className={styles.stepNav}>
        {activeIndex > 0 && (
          <button
            type="button"
            className={styles.stepNavBack}
            onClick={() => setActiveStep(STEPS[activeIndex - 1])}
          >
            <img src={arrowLeftIcon} alt="" />
            {t('transportFields.steps.back')}
          </button>
        )}
        <span className={styles.stepCounter}>
          {t('transportFields.steps.counter', {
            current: activeIndex + 1,
            total: STEPS.length,
          })}
        </span>
        {activeIndex < STEPS.length - 1 && (
          <button
            type="button"
            className={styles.stepNavNext}
            onClick={() => setActiveStep(STEPS[activeIndex + 1])}
          >
            {t('transportFields.steps.next')}
            <img src={arrowRightIcon} alt="" />
          </button>
        )}
      </div>
    </>
  );
};

const AddTransport = ({ onClose, onSuccess, editId }: AddTransportProps) => {
  const [initTransport, setTransport] = useState<any>(null);
  // Шаг хранится здесь, а не в StepsBody: от него зависит вид кнопки отправки в футере
  const [activeStep, setActiveStep] = useState<StepId>('kind');
  const { request, loading, error } = useHttp();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const id = editId || queryParams.get('id');
  const currentLang = useSelector((state: any) => state.settings.lang);
  const { t } = useTranslation();
  const { fetchDocumnets } = useInitialDocument();

  useEffect(() => {
    if (id) {
      TransportService.getTrackParkTransportData(id, currentLang)
        .then(async (res: any) => {
          const transport = Transport.initFormData(res.data);
          if (transport.transport_technical_documents) {
            try {
              transport.transport_technical_documents = await fetchDocumnets(
                transport.transport_technical_documents,
              );
            } catch (e) {}
          }

          setTransport(transport);
        })
        .catch((e: any) => {
          console.log(e.response);
        });
    }
  }, [currentLang, fetchDocumnets, id]);

  const isModalMode = !!onClose;

  return (
    <TransportDictionariesProvider>
      <div className={isModalMode ? styles.modalWrapper : styles.pageWrapper}>
        {isModalMode && (
          <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>{t('park.add_transport_title')}</h2>
            <button className={styles.modalClose} onClick={onClose} type="button" />
          </div>
        )}
        <Formik
          initialValues={initTransport || initializeTransportFields}
          validationSchema={validationSchema(t)}
          enableReinitialize={true}
          onSubmit={(formValues: any, { setSubmitting, setErrors }) => {
            setSubmitting(false);
            const transport = Transport.formData(formValues);
            const formData = serialize(transport, { indices: true });
            formValues.transport_technical_documents &&
              formValues.transport_technical_documents.forEach((el: any) => {
                formData.append('document', el);
              });

            request(TRANSPORT_API + '/trackpark', id ? 'put' : 'post', formData)
              .then(() => {
                if (onSuccess) {
                  onSuccess();
                } else {
                  navigate(PROFILE.park);
                }
              })
              .catch((e: any) => {
                const errors: Record<string, string> = {};
                e.response.data.errors.forEach((error: any) => {
                  const errorPath =
                    error.path[0] === 'document' ? 'transport_technical_documents' : error.path[0];
                  errors[errorPath] = t(error.type);
                });
                setErrors(errors);
              });
          }}
        >
          {({ values, submitCount, errors, dirty }) => (
            <Form>
              <div className={styles.formContent}>
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    {t('transportFields.main_section.heading')}
                  </h3>
                  <div className={styles.hideSubTitle}>
                    <GeneralInformation stacked={isModalMode} />
                  </div>
                </div>

                <div className={styles.divider} />

                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>{t('transportFields.detailed_info')}</h3>
                  <StepsBody
                    values={values}
                    errors={errors as Record<string, unknown>}
                    submitCount={submitCount}
                    activeStep={activeStep}
                    setActiveStep={setActiveStep}
                  />
                </div>
              </div>

              <div className={styles.divider} />

              <div className={styles.formFooter}>
                {submitCount > 0 && Object.keys(errors).length > 0 && (
                  <div className={styles.errorRow}>
                    <Error>{t('errors.mainFormError')}</Error>
                  </div>
                )}
                {error && <div className={styles.errorRow}>{errorToRender(error, t)}</div>}
                {loading && (
                  <div className={styles.loadingRow}>
                    <Loading />
                  </div>
                )}
                <div className={styles.buttons}>
                  {isModalMode && (
                    <button type="button" className={styles.cancelButton} onClick={onClose}>
                      {t('cancel')}
                    </button>
                  )}
                  <button
                    type="submit"
                    className={cn(styles.submitButton, {
                      [styles.submitButtonMuted]: activeStep !== STEPS[STEPS.length - 1],
                    })}
                    disabled={loading || (!dirty && !id)}
                  >
                    {id ? t('edit_save') : t('add')}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </TransportDictionariesProvider>
  );
};

export default AddTransport;
